import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Lightbulb, Loader2, X } from 'lucide-react';
import {
  feedbackService,
  type FeedbackCategory,
  type FeedbackTicket,
} from '../../services/feedbackService';
import { feedbackContextFromPath } from './feedbackContext';
import {
  AttachmentRow,
  MAX_FILES,
  pastedFiles,
  uploadAll,
} from './attachments';
import { useVisualViewportHeight } from '../../hooks/useVisualViewportHeight';

const CATEGORIES: { id: FeedbackCategory; label: string }[] = [
  { id: 'bug', label: 'Bug' },
  { id: 'idea', label: 'Idea' },
  { id: 'process', label: 'Process' },
  { id: 'question', label: 'Question' },
];

const STATUS_LABEL: Record<string, string> = {
  new: 'Received',
  responded: 'Responded',
  reviewing: 'Being reviewed',
  planned: 'Planned',
  done: 'Done',
  declined: 'Not moving forward',
};

/**
 * The light-bulb box. V1 (Jim 2026-08-27) was fire-and-forget; V2 (Jim
 * 2026-09-04) adds the running tally of what you sent, a conversation per
 * ticket ("responded", never closed), a topic, an unread-reply badge on the
 * bulb, "photo / snip" with clipboard paste, and short video clips.
 */
export function FeedbackButton({ context }: { context: 'hub' | 'lite' }) {
  const location = useLocation();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  // The sheet follows the visual viewport, so the phone keyboard shrinks it
  // instead of hiding its foot (Jim 10/28: "it will not let me page down").
  const viewportHeight = useVisualViewportHeight(open);
  const [tab, setTab] = useState<'new' | 'mine'>('new');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data: badge } = useQuery({
    queryKey: ['feedback', 'unread-count'],
    queryFn: () => feedbackService.unreadCount(),
    refetchInterval: 60_000,
    staleTime: 0,
    retry: 1,
  });
  const unread = badge?.unread ?? 0;

  const { data: mine, isLoading: loadingMine } = useQuery({
    queryKey: ['feedback', 'mine'],
    queryFn: () => feedbackService.mine(),
    enabled: open,
    // The app-wide staleTime is 5 minutes; a reply that arrived since the
    // last open must show the moment the panel opens.
    staleTime: 0,
    retry: 1,
  });

  const openPanel = (which: 'new' | 'mine') => {
    setTab(which);
    setSelectedId(null);
    setOpen(true);
  };

  const invalidate = () => qc.invalidateQueries({ queryKey: ['feedback'] });

  return (
    <>
      <button
        type="button"
        onClick={() => openPanel(unread > 0 ? 'mine' : 'new')}
        title={
          unread > 0
            ? `${unread} new repl${unread === 1 ? 'y' : 'ies'} to your feedback`
            : 'Report a bug or share an idea'
        }
        aria-label={
          unread > 0
            ? `Feedback — ${unread} unread repl${unread === 1 ? 'y' : 'ies'}`
            : 'Report a bug or share an idea'
        }
        className={`relative ${
          context === 'lite'
            ? 'w-9 h-9 flex items-center justify-center rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
            : 'p-2 rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
        }`}
      >
        <Lightbulb className="w-5 h-5" />
        {unread > 0 && (
          <span
            data-testid="feedback-unread-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-[1.1rem] text-center"
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-white dark:bg-slate-800 w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-xl p-5 max-h-[92dvh] flex flex-col"
            style={
              viewportHeight ? { maxHeight: viewportHeight - 8 } : undefined
            }
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Feedback"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex gap-1 rounded-lg bg-slate-100 dark:bg-slate-700 p-0.5">
                <button
                  type="button"
                  onClick={() => {
                    setTab('new');
                    setSelectedId(null);
                  }}
                  className={`px-3 py-1 rounded-md text-xs font-semibold ${
                    tab === 'new'
                      ? 'bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-sm'
                      : 'text-slate-500 dark:text-slate-300'
                  }`}
                >
                  New
                </button>
                <button
                  type="button"
                  onClick={() => setTab('mine')}
                  className={`px-3 py-1 rounded-md text-xs font-semibold inline-flex items-center gap-1.5 ${
                    tab === 'mine'
                      ? 'bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-sm'
                      : 'text-slate-500 dark:text-slate-300'
                  }`}
                >
                  My feedback
                  {mine && mine.length > 0 && (
                    <span className="text-[10px] text-slate-400">
                      {mine.length}
                    </span>
                  )}
                  {unread > 0 && (
                    <span className="min-w-[1rem] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-4 text-center">
                      {unread}
                    </span>
                  )}
                </button>
              </div>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="min-h-0 overflow-y-auto overscroll-contain">
              {tab === 'new' ? (
                <NewTicketForm
                  context={context}
                  pathname={location.pathname}
                  onSent={(t) => {
                    invalidate();
                    setSelectedId(t.id);
                    setTab('mine');
                  }}
                />
              ) : (
                <MyFeedback
                  tickets={mine ?? []}
                  loading={loadingMine}
                  selectedId={selectedId}
                  onSelect={setSelectedId}
                  onChanged={invalidate}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function NewTicketForm({
  context,
  pathname,
  onSent,
}: {
  context: 'hub' | 'lite';
  pathname: string;
  onSent: (ticket: FeedbackTicket) => void;
}) {
  const [category, setCategory] = useState<FeedbackCategory>('idea');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addFiles = (incoming: File[]) =>
    setFiles((prev) => [...prev, ...incoming].slice(0, MAX_FILES));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !body.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const keys = await uploadAll(files);
      const ctx = feedbackContextFromPath(pathname);
      const ticket = await feedbackService.create({
        category,
        title: title.trim(),
        body: body.trim(),
        source: context,
        route: ctx.route,
        site_id: ctx.site_id,
        inspection_id: ctx.inspection_id,
        photo_s3_keys: keys,
      });
      setTitle('');
      setBody('');
      setFiles([]);
      onSent(ticket);
    } catch (err) {
      setError(
        (err as Error)?.name === 'AttachmentTooLarge'
          ? (err as Error).message
          : 'Could not send — try again.'
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="flex gap-2">
        {CATEGORIES.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => setCategory(c.id)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
              category === c.id
                ? 'bg-primary-600 text-white border-primary-600'
                : 'border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300'
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        maxLength={200}
        autoFocus
        aria-label="Topic"
        placeholder="Topic — a few words so we can find this conversation later"
        className="w-full text-sm px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-1 focus:ring-primary-500"
      />
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        onPaste={(e) => {
          const pasted = pastedFiles(e);
          if (pasted.length) {
            e.preventDefault();
            addFiles(pasted);
          }
        }}
        rows={4}
        maxLength={4000}
        aria-label="Details"
        placeholder="What happened, or what's your idea? Paste a snip right here. The page you're on comes along automatically."
        className="w-full text-sm px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-1 focus:ring-primary-500"
      />
      <AttachmentRow
        files={files}
        onAdd={addFiles}
        onClear={() => setFiles([])}
      />
      {/* Jim 10/28: on a phone held upright the Send button sat under the
          keyboard and the sheet would not scroll to it; the row stays pinned
          at the foot of the sheet now. */}
      <div className="sticky bottom-0 -mx-5 px-5 pt-2 pb-[max(0.25rem,env(safe-area-inset-bottom))] bg-white dark:bg-slate-800 flex items-center justify-end gap-3">
        <button
          type="submit"
          disabled={busy || !body.trim() || !title.trim()}
          className="flex items-center gap-1.5 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-lg"
        >
          {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          Send
        </button>
      </div>
      {error && (
        <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
      )}
    </form>
  );
}

function MyFeedback({
  tickets,
  loading,
  selectedId,
  onSelect,
  onChanged,
}: {
  tickets: FeedbackTicket[];
  loading: boolean;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onChanged: () => void;
}) {
  const selected = useMemo(
    () => tickets.find((t) => t.id === selectedId) ?? tickets[0] ?? null,
    [tickets, selectedId]
  );

  if (loading) {
    return (
      <p className="text-sm text-slate-500 py-6 text-center">
        <Loader2 className="w-4 h-4 animate-spin inline mr-1.5" />
        Loading your feedback…
      </p>
    );
  }
  if (!tickets.length) {
    return (
      <p className="text-sm text-slate-500 dark:text-slate-400 py-6 text-center">
        Nothing sent yet. Anything you send shows up here with its replies.
      </p>
    );
  }
  return (
    <div className="space-y-3">
      <label className="block">
        <span className="sr-only">Conversation</span>
        <select
          value={selected?.id ?? ''}
          onChange={(e) => onSelect(e.target.value)}
          aria-label="Conversation"
          className="w-full text-sm px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200"
        >
          {tickets.map((t) => (
            <option key={t.id} value={t.id}>
              {t.unread_replies > 0 ? '● ' : ''}
              {t.title || t.body.slice(0, 60)} —{' '}
              {STATUS_LABEL[t.status] ?? t.status}
            </option>
          ))}
        </select>
      </label>
      {selected && (
        <Thread key={selected.id} ticket={selected} onChanged={onChanged} />
      )}
    </div>
  );
}

function Thread({
  ticket,
  onChanged,
}: {
  ticket: FeedbackTicket;
  onChanged: () => void;
}) {
  const [body, setBody] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  // Opening a thread with unread replies clears its share of the badge.
  const markRead = useMutation({
    mutationFn: () => feedbackService.markRead(ticket.id),
    onSuccess: onChanged,
  });
  useEffect(() => {
    if (ticket.unread_replies > 0 && !markRead.isPending) markRead.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket.id, ticket.unread_replies]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'nearest' });
  }, [ticket.messages.length]);

  const reply = useMutation({
    mutationFn: async () => {
      const keys = await uploadAll(files);
      return feedbackService.reply(ticket.id, {
        body: body.trim(),
        attachment_s3_keys: keys,
      });
    },
    onSuccess: () => {
      setBody('');
      setFiles([]);
      setError(null);
      onChanged();
    },
    onError: () => setError('Could not send — try again.'),
  });

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">
            {ticket.title || ticket.body.slice(0, 60)}
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {ticket.category} · sent{' '}
            {new Date(ticket.created_at).toLocaleDateString()}
          </p>
        </div>
        <span
          className={`shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full ${
            ticket.status === 'responded'
              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
              : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
          }`}
        >
          {STATUS_LABEL[ticket.status] ?? ticket.status}
        </span>
      </div>

      <ol className="space-y-2 max-h-[40dvh] overflow-y-auto pr-1">
        <li className="rounded-lg bg-slate-100 dark:bg-slate-700/60 px-3 py-2">
          <p className="text-[11px] text-slate-500 dark:text-slate-400 mb-0.5">
            You · {new Date(ticket.created_at).toLocaleString()}
          </p>
          <p className="text-sm text-slate-800 dark:text-slate-100 whitespace-pre-wrap">
            {ticket.body}
          </p>
          <Attachments urls={ticket.photo_urls} />
        </li>
        {ticket.admin_note && ticket.messages.length === 0 && (
          <li className="rounded-lg bg-emerald-50 dark:bg-emerald-900/30 px-3 py-2">
            <p className="text-[11px] text-emerald-700 dark:text-emerald-300 mb-0.5">
              EnviroLink
            </p>
            <p className="text-sm text-slate-800 dark:text-slate-100 whitespace-pre-wrap">
              {ticket.admin_note}
            </p>
          </li>
        )}
        {ticket.messages.map((m) => (
          <li
            key={m.id}
            data-testid={m.is_admin ? 'admin-message' : 'author-message'}
            className={`rounded-lg px-3 py-2 ${
              m.is_admin
                ? 'bg-emerald-50 dark:bg-emerald-900/30'
                : 'bg-slate-100 dark:bg-slate-700/60'
            }`}
          >
            <p
              className={`text-[11px] mb-0.5 ${
                m.is_admin
                  ? 'text-emerald-700 dark:text-emerald-300'
                  : 'text-slate-500 dark:text-slate-400'
              }`}
            >
              {m.is_admin ? m.author_name || 'EnviroLink' : 'You'} ·{' '}
              {new Date(m.created_at).toLocaleString()}
            </p>
            <p className="text-sm text-slate-800 dark:text-slate-100 whitespace-pre-wrap">
              {m.body}
            </p>
            <Attachments urls={m.attachment_urls} />
          </li>
        ))}
        <div ref={endRef} />
      </ol>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (body.trim() && !reply.isPending) reply.mutate();
        }}
        className="space-y-2"
      >
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onPaste={(e) => {
            const pasted = pastedFiles(e);
            if (pasted.length) {
              e.preventDefault();
              setFiles((prev) => [...prev, ...pasted].slice(0, MAX_FILES));
            }
          }}
          rows={2}
          maxLength={4000}
          aria-label="Reply"
          placeholder="Reply — the conversation stays open"
          className="w-full text-sm px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-1 focus:ring-primary-500"
        />
        <div className="flex items-center justify-between gap-3">
          <AttachmentRow
            files={files}
            onAdd={(f) =>
              setFiles((prev) => [...prev, ...f].slice(0, MAX_FILES))
            }
            onClear={() => setFiles([])}
          />
          <button
            type="submit"
            disabled={reply.isPending || !body.trim()}
            className="flex items-center gap-1.5 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-lg"
          >
            {reply.isPending && (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            )}
            Reply
          </button>
        </div>
        {error && (
          <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
        )}
      </form>
    </div>
  );
}

function Attachments({ urls }: { urls: string[] }) {
  if (!urls.length) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-1.5">
      {urls.map((u, i) => (
        <a
          key={i}
          href={u}
          target="_blank"
          rel="noreferrer"
          className="text-xs text-sky-700 dark:text-sky-400 underline"
        >
          attachment {i + 1}
        </a>
      ))}
    </div>
  );
}
